'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { HeatmapLayer } from '@deck.gl/aggregation-layers';
import { useDebounce } from 'use-debounce';
import {
  useGetEngageMapMembersQuery,
  useGetEngageStrategyHeatmapQuery,
  useGetEngageStrategyDetailsQuery,
} from 'src/api/redux';
import useProfileStrategies from 'src/hooks/strategy/useProfileStrategies';
import { extractProviderLocation } from 'src/components/Strategy/Profiles/utils/locationUtils';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { MIN_ZOOM, CENTER } from '../../constants';
interface StrategyProfileMapContentProps {
  engageStrategyId: string | null;
}

const StrategyProfileMapContent = ({
  engageStrategyId,
}: StrategyProfileMapContentProps) => {
  const { entityDetails } = useProfile();
  const map = useMap();
  const hasFittedBounds = useRef(false);
  const deckOverlayRef = useRef<GoogleMapsOverlay | null>(null);

  // Get source provider location from profile data
  const sourceProviderLocation = useMemo(
    () => extractProviderLocation(entityDetails),
    [entityDetails]
  );

  const {
    search,
    sortValues,
    bounds,
    selectedSpecialties,
    selectedStatuses,
    selectedProviderLists,
    zoom,
    showHeatmap,
    selectedHeatmapMetric,
  } = useProfileStrategies();

  // Debounce zoom for smooth radius transitions
  const [debouncedZoom] = useDebounce(zoom, 300);

  // Convert bounds for API query
  const apiQueryBounds = useMemo(() => {
    if (!bounds || (bounds.min_lat === 0 && bounds.max_lat === 0)) return null;
    return {
      north: bounds.max_lat,
      south: bounds.min_lat,
      east: bounds.max_lng,
      west: bounds.min_lng,
    };
  }, [bounds]);

  // Shared filter parameters for map endpoint
  const filterParams = {
    strategyId: engageStrategyId,
    provider_id: entityDetails?.id,
    q: search,
    order: sortValues.order,
    sort: sortValues.sort,
    ...(sortValues.sort_metric && { sort_metric: sortValues.sort_metric }),
    bounds: apiQueryBounds,
    specialties: selectedSpecialties,
    statuses: selectedStatuses,
    provider_lists: selectedProviderLists,
    metric: showHeatmap ? selectedHeatmapMetric?.code : undefined,
  };
  // Map endpoint - for markers
  const {
    data: engageMapMembers,
  } = useGetEngageMapMembersQuery(filterParams, {
    skip: !engageStrategyId || !entityDetails?.id,
  });

  // Heatmap metric data - for heatmap layer with selectedHeatmapMetric
  const { data: hmdata, isFetching: isHeatmapFetching } = useGetEngageStrategyHeatmapQuery(
    {
      strategyId: engageStrategyId || '',
      providerId: entityDetails?.id || '',
      params: { metric: showHeatmap ? selectedHeatmapMetric?.code : undefined },
    },
    {
      skip:
        !engageStrategyId ||
        !entityDetails?.id ||
        !showHeatmap ||
        !selectedHeatmapMetric,
    }
  );

  // Fetch engage strategy details to get direction (inbound vs outbound)
  const { data: engageStrategyDetails } = useGetEngageStrategyDetailsQuery(
    engageStrategyId || '',
    {
      skip: !engageStrategyId,
    }
  );

  const isNotReferring = engageStrategyDetails?.type === 'NOT_REFERRING';
  // Convert radius from miles to meters (API returns miles, Google Maps expects meters)
  const radiusInMeters = (engageStrategyDetails?.radius ?? 0) * 1609.34;

  // Use primitive values for dependencies to avoid reference changes
  const memberCount = engageMapMembers?.length ?? 0;
  const hasSourceLocation = !!sourceProviderLocation;
  const sourceLat = sourceProviderLocation?.lat;
  const sourceLng = sourceProviderLocation?.lng;

  // Create heatmap data - use hmdata.points with volume if metric selected, else use engageMapMembers with weight=1
  const heatmapData = useMemo(() => {
    // If a metric is selected, we MUST use hmdata - don't fall back to engageMapMembers
    if (selectedHeatmapMetric) {
      // Wait for hmdata to load before showing anything (also clear during fetching to avoid stale data)
      if (isHeatmapFetching || !hmdata?.points || !Array.isArray(hmdata.points)) {
        return [];
      }
      return hmdata.points
        .filter(
          (point: { lat?: number; lng?: number; volume?: number }) =>
            point.lat &&
            point.lng &&
            typeof point.lat === 'number' &&
            typeof point.lng === 'number'
        )
        .map((point: { lat: number; lng: number; volume: number }) => ({
          position: [point.lng, point.lat],
          weight: point.volume || 1,
        }));
    }

    // No metric selected - use engageMapMembers with weight=1 for provider count
    if (!engageMapMembers || !Array.isArray(engageMapMembers)) return [];

    return engageMapMembers
      .filter(
        (member: { lat?: number; lng?: number }) =>
          member.lat &&
          member.lng &&
          typeof member.lat === 'number' &&
          typeof member.lng === 'number'
      )
      .map((member: { lat: number; lng: number }) => ({
        position: [member.lng, member.lat],
        weight: 1, // Provider count - each provider counts as 1
      }));
  }, [engageMapMembers, selectedHeatmapMetric, hmdata, isHeatmapFetching]);

  // Initialize deck.gl overlay once when map is available
  useEffect(() => {
    if (!map) return;

    // Create new overlay instance
    const overlay = new GoogleMapsOverlay({ interleaved: false });
    overlay.setMap(map);
    deckOverlayRef.current = overlay;

    return () => {
      overlay.setMap(null);
      overlay.finalize();
      deckOverlayRef.current = null;
    };
  }, [map]);

  // Update heatmap layer when data or settings change
  useEffect(() => {
    if (!deckOverlayRef.current) return;

    // Create heatmap layer if enabled and has data
    if (showHeatmap && heatmapData.length > 0) {
      // Calculate dynamic radius based on zoom
      let radius = 15;
      const minZoom = 4;
      const maxZoom = 20;
      const minRadius = 15;
      const maxRadius = 100;

      if (debouncedZoom >= maxZoom) {
        radius = maxRadius;
      } else if (debouncedZoom <= minZoom) {
        radius = minRadius;
      } else {
        radius =
          minRadius +
          ((maxRadius - minRadius) * (debouncedZoom - minZoom)) /
            (maxZoom - minZoom);
      }

      // Use metric code in layer ID to force re-render when metric changes
      const layerId = selectedHeatmapMetric?.code
        ? `strategy-profile-heatmap-layer-${selectedHeatmapMetric.code}`
        : 'strategy-profile-heatmap-layer-default';

      const heatmapLayer = new HeatmapLayer({
        id: layerId,
        data: heatmapData,
        aggregation: 'SUM',
        getPosition: (d: { position: [number, number] }) => d.position,
        getWeight: (d: { weight: number }) => d.weight,
        radiusPixels: 30 + radius,
        intensity: 0.9,
        threshold: 0.01,
        opacity: 0.6,
        colorRange: [
          [255, 255, 178], // Light yellow
          [254, 204, 92], // Yellow-orange
          [253, 141, 60], // Orange
          [240, 59, 32], // Red-orange
          [189, 0, 38], // Red
          [128, 0, 38], // Dark red
          [103, 0, 113], // Purple
          [63, 0, 125], // Dark purple
        ],
      });

      deckOverlayRef.current.setProps({ layers: [heatmapLayer] });
    } else {
      // Clear layers when heatmap is disabled or no data
      deckOverlayRef.current.setProps({ layers: [] });
    }
  }, [showHeatmap, heatmapData, debouncedZoom, selectedHeatmapMetric?.code]);

  // Reset fitBounds flag when source provider changes (e.g., navigating between profiles)
  useEffect(() => {
    hasFittedBounds.current = false;
  }, [sourceLat, sourceLng]);

  useEffect(() => {
    // For NOT_REFERRING strategies with radius, check if we need to re-fit bounds
    // This handles the case where bounds were fitted before radius data loaded
    // Only reset if user hasn't zoomed (zoom is still at MIN_ZOOM)
    if (
      isNotReferring &&
      radiusInMeters > 0 &&
      hasFittedBounds.current &&
      zoom === MIN_ZOOM
    ) {
      hasFittedBounds.current = false;
    }

    // Only fit bounds once on initial load (unless reset above)
    if (hasFittedBounds.current) {
      return;
    }

    // Check all required conditions are met
    if (!map) {
      return;
    }

    // Wait for data to load before fitting (but allow empty results if we have a source provider)
    if (!engageMapMembers) {
      return;
    }

    // For NOT_REFERRING strategies, wait for radius data before fitting bounds
    if (isNotReferring && radiusInMeters === 0) {
      return;
    }

    // Edge case: No source provider and no members - fallback to center of USA
    if (!sourceProviderLocation && engageMapMembers.length === 0) {
      map.setCenter(CENTER);
      map.setZoom(MIN_ZOOM);
      hasFittedBounds.current = true;
      return;
    }

    // Create bounds for fitting
    const bounds = new google.maps.LatLngBounds();

    // Add source provider to bounds (ensure it's always visible)
    if (sourceProviderLocation) {
      bounds.extend(
        new google.maps.LatLng(
          sourceProviderLocation.lat,
          sourceProviderLocation.lng
        )
      );
    }

    // Add circle bounds if NOT_REFERRING strategy
    if (isNotReferring && sourceProviderLocation && radiusInMeters > 0) {
      // Calculate circle bounds to ensure full circle is visible
      const circleBounds = new google.maps.Circle({
        center: sourceProviderLocation,
        radius: radiusInMeters,
      }).getBounds();

      if (circleBounds) {
        bounds.union(circleBounds);
      }
    }

    // Add all engage member markers to bounds
    if (engageMapMembers && Array.isArray(engageMapMembers)) {
      engageMapMembers.forEach((member) => {
        if (member.lat && member.lng) {
          bounds.extend(new google.maps.LatLng(member.lat, member.lng));
        }
      });
    }

    // Fit bounds if we have at least one valid point
    if (!bounds.isEmpty()) {
      // Mark as fitted BEFORE calling fitBounds to prevent re-entry
      hasFittedBounds.current = true;

      // Use requestAnimationFrame to ensure fitBounds happens in the next frame
      // This ensures the map is fully initialized and prevents race conditions
      requestAnimationFrame(() => {
        map.fitBounds(bounds);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    memberCount,
    hasSourceLocation,
    sourceLat,
    sourceLng,
    isNotReferring,
    radiusInMeters,
  ]);
  // Note: Using primitive values (memberCount, hasSourceLocation, sourceLat, sourceLng) to prevent unnecessary re-renders
  // engageMapMembers and sourceProviderLocation are intentionally excluded - we use their primitive values instead
  // zoom is intentionally excluded from deps to prevent re-running when fitBounds changes zoom

  return null;
};

export default StrategyProfileMapContent;
